import { ListCellFallback } from '@cbhq/cds-web/cells';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { AuditEditDetailSection } from '../../../Activity/components/AuditEditDetailSection';
import type { AuditEditDetailRowEntry } from '../../../Activity/types';

const messages = defineMessages({
  recentActivityHeading: {
    id: 'Team.editMemberTray.recentActivityHeading',
    defaultMessage: 'Recent activity',
    description: 'Heading for the recent activity section of the edit member tray',
  },
  noRecentActivity: {
    id: 'Team.editMemberTray.noRecentActivity',
    defaultMessage: 'No recent activity',
    description: 'Message shown when a member has no recent audit log activity',
  },
  viewAll: {
    id: 'Team.editMemberTray.viewAll',
    defaultMessage: 'View all',
    description: 'Link to view all activity for a member',
  },
  viewAllAccessibilityLabel: {
    id: 'Team.editMemberTray.viewAllAccessibilityLabel',
    defaultMessage: 'View all activity',
    description: 'Accessibility label for the view all activity link',
  },
});

const ACTION_STYLE = { cursor: 'pointer' } as const;

export type RecentActivitySectionProps = {
  rows: AuditEditDetailRowEntry[];
  isLoading: boolean;
  onViewAll: () => void;
};

type RecentActivityContentProps = {
  rows: AuditEditDetailRowEntry[];
  isLoading: boolean;
  emptyMessage: string;
};

const SKELETON_ROWS = [0, 1, 2, 3, 4] as const;

function RecentActivitySkeleton() {
  return (
    <VStack width="100%" gap={0} paddingX={1} testID="recent-activity-skeleton">
      {SKELETON_ROWS.map((row) => (
        <ListCellFallback
          key={row}
          spacingVariant="condensed"
          title
          detail
          disableRandomRectWidth
          rectWidthVariant={row}
        />
      ))}
    </VStack>
  );
}

function RecentActivityContent({ rows, isLoading, emptyMessage }: RecentActivityContentProps) {
  if (isLoading) {
    return <RecentActivitySkeleton />;
  }

  if (rows.length > 0) {
    return <AuditEditDetailSection rows={rows} />;
  }

  return (
    <Text font="body" color="fgMuted" paddingX={4}>
      {emptyMessage}
    </Text>
  );
}

export function RecentActivitySection({ rows, isLoading, onViewAll }: RecentActivitySectionProps) {
  const { formatMessage } = useIntl();
  const hasRows = rows.length > 0;

  return (
    <VStack width="100%" gap={1.5} paddingY={1.5}>
      <HStack width="100%" paddingX={4} alignItems="center" justifyContent="space-between">
        <Text font="headline">{formatMessage(messages.recentActivityHeading)}</Text>
        {hasRows && !isLoading ? (
          <Pressable
            onClick={onViewAll}
            background="transparent"
            noScaleOnPress
            accessibilityLabel={formatMessage(messages.viewAllAccessibilityLabel)}
          >
            <Text
              as="span"
              font="label2"
              color="fgMuted"
              textDecoration="underline"
              style={ACTION_STYLE}
            >
              {formatMessage(messages.viewAll)}
            </Text>
          </Pressable>
        ) : null}
      </HStack>
      <RecentActivityContent
        rows={rows}
        isLoading={isLoading}
        emptyMessage={formatMessage(messages.noRecentActivity)}
      />
    </VStack>
  );
}
